/**
 * Shared utilities for client- and server-safe code (prompt helpers, formatting, stable identities, etc.).
 */

import type { LabelVariant } from "@/components/atoms/Label";
import { PPTX_NO_TEXT_ERROR, PPTX_NO_SLIDES_ERROR, PDF_NO_TEXT_ERROR } from "@/constants/deckParse";
import { AGENT_CLIENT, AUTHOR_TYPE, DOCUMENT_STATUS, type DocumentStatus } from "@/lib/types";
import { PLACEHOLDER_TOKEN as FOUNDER_INPUT_PLACEHOLDER } from "@/lib/utils/founderInputNotes";

const LINKEDIN_PROFILE_BASE_URL = "https://www.linkedin.com/in/";

/**
 * Selectors for the portal'd markers Radix leaves in the DOM while an
 * interactive popup layer (Select listbox, DropdownMenu, Popover, Combobox)
 * is OPEN. Each of these elements is only mounted while its layer is open, so
 * their mere presence signals "a dropdown/menu is currently open".
 *
 * Deliberately excludes tooltips (`role="tooltip"`): a tooltip should never
 * block a modal from dismissing on backdrop click.
 */
const RADIX_OPEN_POPUP_LAYER_SELECTOR = [
  "[data-radix-popper-content-wrapper]",
  "[data-radix-select-content]",
  "[data-radix-popover-content]",
  "[data-radix-dropdown-menu-content]",
  '[role="listbox"]',
  '[role="menu"]',
].join(",");

/**
 * True when a Radix portal'd popup layer (Select dropdown, DropdownMenu, etc.)
 * is currently open anywhere in the document.
 *
 * Hand-rolled modal overlays that dismiss on outside-pointer (the
 * `event.target === event.currentTarget` pattern) are NOT Radix
 * DismissableLayers, so they don't participate in Radix's layer-stack
 * coordination. Without this guard, a single backdrop click meant to dismiss
 * an open dropdown ALSO closes the surrounding modal (LEV-993). Call this from
 * the overlay's `onPointerDown` handler and skip the close when it returns
 * true — the click dismisses the dropdown layer and the modal stays open.
 *
 * MUST be called from a pointer*down* handler on the overlay: the overlay's
 * React listener (bound on the root container) runs before Radix's
 * document-level dismiss listener, so the open layer is still mounted at the
 * moment we check. By the following `mousedown`/`click`, Radix has already
 * closed and unmounted the layer.
 */
export function isRadixPopupLayerOpen(): boolean {
  if (typeof document === "undefined") return false;
  return document.querySelector(RADIX_OPEN_POPUP_LAYER_SELECTOR) != null;
}

export interface AcceleratorSuggestionIdentityInput {
  id?: string | null;
  section?: string | null;
  priority?: string | null;
  issue?: string | null;
  suggestion?: string | null;
  question?: string | null;
}

function normalizeSuggestionIdentityPart(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function hashString(value: string): string {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

export function getAcceleratorSuggestionIdentity(
  suggestion: AcceleratorSuggestionIdentityInput,
): string {
  const contentKey = [
    normalizeSuggestionIdentityPart(suggestion.section),
    normalizeSuggestionIdentityPart(suggestion.priority),
    normalizeSuggestionIdentityPart(suggestion.issue),
    normalizeSuggestionIdentityPart(suggestion.suggestion),
    normalizeSuggestionIdentityPart(suggestion.question),
  ]
    .filter(Boolean)
    .join("|");
  const fallbackKey = normalizeSuggestionIdentityPart(suggestion.id) || "suggestion";
  return `sug-${hashString(contentKey || fallbackKey)}`;
}

export function normalizeAcceleratorSuggestionIds<
  T extends AcceleratorSuggestionIdentityInput,
>(suggestions: T[]): T[] {
  const counts = new Map<string, number>();

  return suggestions.map((suggestion) => {
    const baseId = getAcceleratorSuggestionIdentity(suggestion);
    const count = (counts.get(baseId) ?? 0) + 1;
    counts.set(baseId, count);
    return {
      ...suggestion,
      id: count === 1 ? baseId : `${baseId}-${count}`,
    };
  });
}

function isAcceleratorSuggestionStableId(identity: string): boolean {
  return /^sug-[a-z0-9]+(?:-\d+)?$/.test(identity);
}

export function acceleratorSuggestionMatchesIdentity(
  suggestion: AcceleratorSuggestionIdentityInput,
  identity: string,
): boolean {
  if (suggestion.id === identity) return true;
  if (suggestion.id && isAcceleratorSuggestionStableId(suggestion.id)) {
    return false;
  }
  return getAcceleratorSuggestionIdentity(suggestion) === identity;
}

/** Coalesce a blank/missing string to the fallback label. */
export function coalesceToUnknown(
  value: string | null | undefined,
  fallback = "[Unknown – not provided]",
): string {
  return value && value.trim().length > 0 ? value : fallback;
}

const LINKEDIN_HANDLE_RE = /^[a-zA-Z][a-zA-Z0-9-]{1,99}$/;

function extractLinkedinProfileSlug(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  if (trimmed.toLowerCase().includes("linkedin.com")) {
    try {
      const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      const url = new URL(normalized);
      const parts = url.pathname.split("/").filter(Boolean);
      const inIndex = parts.findIndex((part) => part.toLowerCase() === "in");
      if (inIndex >= 0) {
        const slug = parts[inIndex + 1] ?? "";
        return slug.replace(/^\/+/, "").replace(/\/+$/, "");
      }
      const host = url.hostname.toLowerCase();
      if (
        parts.length === 1 &&
        LINKEDIN_HANDLE_RE.test(parts[0]) &&
        (host === "linkedin.com" || host.endsWith(".linkedin.com"))
      ) {
        return parts[0];
      }
      return "";
    } catch {
      /* benign: malformed URL — return empty slug */
      return "";
    }
  }

  return trimmed.replace(/^\/+/, "").replace(/\/+$/, "");
}

/**
 * Normalize a SINGLE pasted LinkedIn profile URL to the canonical
 * `https://www.linkedin.com/in/<slug>` form used across the people domain
 * (Person dedupe keys, Universe identity keys). Returns null for anything
 * that is not a real linkedin.com profile URL — including lookalike hosts
 * (linkedin.com.evil.io) and company/School pages without an /in/ slug.
 */
export function normalizeLinkedinProfileUrl(raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  let isLinkedinHost = false;
  try {
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const host = new URL(withScheme).hostname.toLowerCase();
    isLinkedinHost = host === "linkedin.com" || host.endsWith(".linkedin.com");
  } catch {
    /* benign: invalid URL — fall through to bare-handle check */
  }

  if (isLinkedinHost) {
    const slug = extractLinkedinProfileSlug(trimmed);
    return slug ? `${LINKEDIN_PROFILE_BASE_URL}${slug.toLowerCase()}` : null;
  }

  if (LINKEDIN_HANDLE_RE.test(trimmed)) {
    return `${LINKEDIN_PROFILE_BASE_URL}${trimmed.toLowerCase()}`;
  }

  return null;
}

export function normalizeLinkedinProfileUrls(rawUrls: string[]): string[] {
  const seen = new Set<string>();
  const urls: string[] = [];

  for (const raw of rawUrls) {
    const slug = extractLinkedinProfileSlug(raw);
    if (!slug) continue;

    const canonical = `${LINKEDIN_PROFILE_BASE_URL}${slug.toLowerCase()}`;
    const key = canonical.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    urls.push(canonical);
    if (urls.length >= 10) break;
  }

  return urls;
}

/**
 * Format a project name for display: capped at 5 words and 35 characters,
 * ellipsis only when truncated, stable `Project <id>` label when name is empty.
 */
export function formatProjectLabel(
  sourceName: string | null | undefined,
  projectId: string,
): string {
  const trimmedName = (sourceName || "").trim();

  if (!trimmedName) {
    return `Project ${projectId.substring(0, 8)}`;
  }

  const words = trimmedName.split(/\s+/).filter(Boolean);
  const firstFiveWords = words.slice(0, 5).join(" ");

  if (firstFiveWords.length > 35) {
    return firstFiveWords.substring(0, 35) + "...";
  }

  return firstFiveWords;
}

/** Map evaluation rating string to Label variant. */
export function ratingToVariant(rating: string): LabelVariant {
  switch (rating) {
    case "Strong":
      return "strong";
    case "Good":
      return "good";
    case "Average":
      return "average";
    case "Critical Gap":
    case "Needs Improvement":
      return "critical";
    default:
      return "average";
  }
}

/**
 * Map letter grade (A/B/C/D/F) to Label variant.
 */
export function gradeToVariant(grade: string): LabelVariant {
  switch (grade) {
    case "A":
      return "strong";
    case "B":
      return "good";
    case "C":
      return "average";
    case "D":
    case "F":
      return "critical";
    default:
      return "average";
  }
}

/**
 * Map rubricType value to a display label.
 */
export function rubricTypeLabel(rubricType?: string | null): string {
  switch (rubricType) {
    case "bootstrap":
      return "Bootstrap rubric";
    case "venture":
      return "Venture rubric";
    default:
      return "Unknown rubric";
  }
}

/**
 * Get the UTF-8 byte size of a string. UTF-8 is variable-width (1-4 bytes per
 * char), so string.length is not the byte size — which matters when checking
 * against byte limits like MAX_UPLOAD_BYTES.
 */
export function getUtf8ByteSize(str: string): number {
  return new TextEncoder().encode(str).length;
}

/**
 * Strip a Markdown code fence that wraps an ENTIRE text, e.g.
 * "```markdown\n## Doc...\n```" → "## Doc...".
 *
 * LLMs (observed on gpt-5.4-mini, ~1/5 of idea-statement generations)
 * intermittently wrap a whole document in a fence, which then renders as one
 * giant code block. Only a clean whole-text wrapper is stripped, with a
 * deliberately conservative guard: any fence-looking line inside the body
 * disables stripping (even where Markdown would allow a longer outer fence
 * to legally contain shorter inner ones), so documents with interior code
 * blocks are never touched. Per Markdown
 * fence rules, the closing fence must be at least as long as the opener
 * (enforced via backreference) — a shorter trailing backtick run is not a
 * valid closer, so such text is left unchanged.
 */
export function stripWrappingCodeFence(text: string): string {
  // (?!`) pins the captured opener to the full backtick run — without it,
  // backtracking lets a ````-opener capture only ``` and match a shorter closer.
  const match = /^(`{3,})(?!`)[^\n]*\n([\s\S]*?)\n\1`*\s*$/.exec(text.trim());
  if (!match) return text;
  const inner = match[2];
  if (/^\s*`{3,}/m.test(inner)) return text;
  return inner.trim();
}

/**
 * House style, applied mechanically: an em-dash or en-dash between words
 * becomes a comma; one used as a range separator between digits becomes
 * "to". A dash opening a markdown line (a quote attribution, a dash-led list
 * item) is structural, not a word-joiner, so it is dropped rather than
 * turned into a leading comma, which would read as a syntax error there.
 *
 * MUST pad with `[ \t]*`, never `\s*`: `\s` matches the newline on either
 * side of the dash, so a dash that happens to sit at a line break gets
 * rewritten into ", " and splices the two lines into one, destroying a
 * markdown paragraph break. `[ \t]*` only ever eats horizontal whitespace,
 * so a newline next to the dash survives untouched.
 */
export function stripDashes(text: string): string {
  return text
    .replace(/^[ \t]*[—–][ \t]*/gm, "")
    // A figure with a unit or currency sign right at the dash ("$8k–$10k",
    // "10%–20%") reads as a range ONLY when the dash is unspaced: a spaced
    // dash next to the same kind of figures ("raised $2M — 40 customers")
    // is two clauses that happen to flank numbers, not a range, so it must
    // fall through to the word-join comma rule below instead.
    .replace(/(\d[%kKmMbB])[—–]([$£€]?\d)/g, "$1 to $2")
    .replace(/(\d)[—–]([$£€]\d)/g, "$1 to $2")
    // A plain-digit range (no unit or currency attached to either figure)
    // keeps today's behavior: the dash still reads as "to" whether or not
    // it is spaced.
    .replace(/(\d)[ \t]*[—–][ \t]*(\d)/g, "$1 to $2")
    // A dash ending a line gets a bare comma: the general ", " rule would
    // leave a space before the newline. Trailing spaces elsewhere are left
    // alone, since two of them are a markdown hard line break.
    .replace(/[ \t]*[—–][ \t]*(?=\n|$)/g, ",")
    .replace(/[ \t]*[—–][ \t]*/g, ", ")
    .replace(/[ \t]+,/g, ",")
    .replace(/,[ \t]*,/g, ",");
}

/**
 * The one email to show for a Person with multiple PersonEmail rows: the
 * `isPrimary` row wins, else the first, else null. Matches the rule Orbit's
 * People table and AddPersonSheet use, so every surface picks the same email
 * for the same Person.
 */
export function pickPrimaryEmail(
  emails: ReadonlyArray<{ email: string; isPrimary: boolean }> | undefined,
): string | null {
  if (!emails || emails.length === 0) return null;
  return emails.find((e) => e.isPrimary)?.email ?? emails[0]?.email ?? null;
}

/**
 * Maximum upload size in bytes.
 * This is a hard Vercel upload limit, so we must check UTF-8 byte size, not character length.
 */
export const MAX_UPLOAD_BYTES = 4.5 * 1024 * 1024;

/**
 * Default locale for date/time formatting.
 * Using a fixed locale prevents hydration mismatches between server and client.
 */
export const DEFAULT_LOCALE = "en-US";

/**
 * Format an integer count with thousands separators (e.g. 1234 → "1,234")
 * (LEV-1076). Uses the fixed `DEFAULT_LOCALE` so the separator is stable across
 * server/client renders. For human-facing counts on Orbit (tab badges, roster
 * sizes) where large numbers read more naturally with commas.
 */
export function formatCount(count: number): string {
  return count.toLocaleString(DEFAULT_LOCALE);
}

/**
 * Format a date as a short date string (e.g., "Jan 9").
 * Uses fixed locale to prevent hydration mismatches.
 */
export function formatShortDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(DEFAULT_LOCALE, { month: "short", day: "numeric" });
}

/**
 * Format a date as a short date string with a two-digit day (e.g., "Jan 09").
 * Uses fixed locale to prevent hydration mismatches.
 */
export function formatShortDateWithPaddedDay(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(DEFAULT_LOCALE, { month: "short", day: "2-digit" });
}

/**
 * Format a UTC-midnight, date-only instant (e.g. a task's `scheduledFor`) as
 * "Oct 23". Formats in UTC: local time would show the day before in a US timezone.
 */
export function formatUtcShortDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(DEFAULT_LOCALE, { month: "short", day: "numeric", timeZone: "UTC" });
}

/**
 * Format a date as a date-time string (e.g., "Jan 9, 2:30 PM").
 * Uses fixed locale to prevent hydration mismatches.
 */
export function formatDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString(DEFAULT_LOCALE, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a date range for display (e.g., "Mar 18 - Mar 24").
 * Uses fixed locale to prevent hydration mismatches.
 */
export function formatWeekDateRange(start: Date | string, end: Date | string): string {
  // The weekly-report week window is computed in UTC (Mon 00:00 → Sun), so format
  // in UTC — otherwise a negative-offset (US) timezone renders the Sunday end as
  // Saturday.
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  };
  const startDate = typeof start === "string" ? new Date(start) : start;
  const endDate = typeof end === "string" ? new Date(end) : end;
  const startStr = startDate.toLocaleDateString(DEFAULT_LOCALE, options);
  const endStr = endDate.toLocaleDateString(DEFAULT_LOCALE, options);
  return `${startStr} - ${endStr}`;
}

/**
 * Format a date+time stamp for the version-history panel (e.g., "May 5, 2026 · 1:53 PM").
 * Uses fixed locale to prevent hydration mismatches between server and client.
 */
const AGENT_CLIENT_NAMES: Record<string, string> = {
  [AGENT_CLIENT.CLAUDE]: "Claude",
  [AGENT_CLIENT.OPENAI]: "OpenAI",
};

/**
 * Lower-case display name for a version saved by the founder's own external
 * agent ("your Claude agent"), or null for every other author so callers keep
 * their Lev / You wording. Mid-sentence form; callers capitalise to start a line.
 */
export function versionAgentLabel(
  authorType: string,
  agentClient: string | null | undefined,
): string | null {
  if (authorType !== AUTHOR_TYPE.AGENT) return null;
  const name = agentClient ? AGENT_CLIENT_NAMES[agentClient] : undefined;
  return name ? `your ${name} agent` : "your agent";
}

/**
 * Sentence-start label for a change an external agent made through Lev's MCP
 * server: "Your Claude agent", or with another person's possessive ("Sam's
 * Claude agent") when the agent acted for someone other than the viewer. An
 * unrecognised or missing client still reads "Your agent" / "Sam's agent":
 * every MCP write is agent-made, so it must never read as a person's own.
 */
export function agentActorLabel(
  agentClient: string | null | undefined,
  possessive: string = "Your",
): string {
  const name = agentClient ? AGENT_CLIENT_NAMES[agentClient] : undefined;
  return name ? `${possessive} ${name} agent` : `${possessive} agent`;
}

/**
 * Whether a persisted Version.authorType counts as the founder editing the
 * block. An "agent" version is saved by the founder's own external agent
 * (LEV-2496) and is founder-directed, so it counts the same as "user".
 */
export function isFounderAuthoredVersion(authorType: string): boolean {
  return authorType === AUTHOR_TYPE.USER || authorType === AUTHOR_TYPE.AGENT;
}

/**
 * Client counterpart of isFounderAuthoredVersion for hydrated store versions.
 * Hydration maps agent versions to authorType "AI" (the fresh-edit highlight
 * depends on it), so agentLabel is the only signal that one is founder-directed.
 */
export function isFounderDirectedDocumentVersion(version: {
  authorType: string;
  agentLabel?: string | null;
}): boolean {
  return version.authorType === "User" || version.agentLabel != null;
}

export function formatVersionTimestamp(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const datePart = d.toLocaleDateString(DEFAULT_LOCALE, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString(DEFAULT_LOCALE, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${datePart} · ${timePart}`;
}

/**
 * Format a date as relative time (e.g., "5 minutes ago", "2 days ago").
 * Falls back to short date for dates older than 7 days.
 * Uses fixed locale to prevent hydration mismatches.
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

  return formatShortDate(date);
}

/**
 * Wall-clock reading of `date` in an IANA zone, as plain integers. `h23` so
 * midnight reads 0, never 24. Whole seconds only — `Intl` carries no
 * millisecond part, which `zoneOffsetMs` accounts for.
 */
function wallClockIn(timeZone: string, date: Date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

/** The zone's offset from UTC at `date`, in ms, positive east of Greenwich. */
function zoneOffsetMs(timeZone: string, date: Date): number {
  const w = wallClockIn(timeZone, date);
  const wallAsUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  const wholeSeconds = Math.floor(date.getTime() / 1000) * 1000;
  return wallAsUtc - wholeSeconds;
}

/**
 * The instant the calendar day containing `now` began in `timeZone` — the first
 * moment whose wall-clock date there is `now`'s date — returned as a UTC `Date`.
 *
 * The offset comes from the zone's own rules via `Intl`, never from a fixed
 * number, so DST is honoured: in `America/Los_Angeles` the day after
 * spring-forward starts 23 hours after the previous one, the day after
 * fall-back 25.
 *
 * Two candidate offsets are read — at the naive guess, then at the instant that
 * guess produced — and the EARLIEST candidate that lands on the target date
 * wins. Neither candidate alone is right for every zone: when a transition falls
 * on midnight itself (Santiago, Havana), the first is right for spring-forward
 * (the day begins at 01:00, there is no 00:00) and the second for fall-back,
 * and a fixed choice of either returns 23:00 of the previous day in the other
 * case. For a zone that does not transition at midnight both candidates agree.
 */
export function startOfDayInTimeZone(timeZone: string, now: Date): Date {
  const wall = wallClockIn(timeZone, now);
  const midnightAsUtc = Date.UTC(wall.year, wall.month - 1, wall.day);
  const first = midnightAsUtc - zoneOffsetMs(timeZone, new Date(midnightAsUtc));
  const second = midnightAsUtc - zoneOffsetMs(timeZone, new Date(first));
  const onTargetDate = (instant: number) => {
    const w = wallClockIn(timeZone, new Date(instant));
    return w.year === wall.year && w.month === wall.month && w.day === wall.day;
  };
  const candidates = [first, second].filter(onTargetDate);
  return new Date(candidates.length === 0 ? second : Math.min(...candidates));
}

/**
 * Calculate the current ISO-8601 week number (UTC-based).
 * Week 1 is the week containing January 4th.
 * Uses the Thursday-based algorithm: the week containing the year's first Thursday.
 */
export function getWeekNumber(date?: Date): number {
  const d = new Date(date ?? Date.now());
  d.setUTCHours(0, 0, 0, 0);
  // Set to nearest Thursday: current date + 4 - current day number (Mon=1..Sun=7)
  const dayNum = d.getUTCDay() || 7; // Convert Sunday from 0 to 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil(
    ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
  );
  return weekNo;
}

/**
 * Calculate project-relative week number.
 * Week 1 = the Monday-Sunday week containing project creation date.
 * Subsequent weeks are consecutive Monday-Sunday periods.
 *
 */
export function getProjectWeekNumber(projectCreatedAt: Date, date?: Date): number {
  const d = new Date(date ?? Date.now());
  const created = new Date(projectCreatedAt);

  // Find Monday of the week containing project creation
  // getUTCDay: Sun=0, Mon=1, ..., Sat=6. Convert to Mon=1, ..., Sun=7
  const createdDayOfWeek = created.getUTCDay() || 7;
  const week1Monday = new Date(created);
  week1Monday.setUTCDate(created.getUTCDate() - createdDayOfWeek + 1);
  week1Monday.setUTCHours(0, 0, 0, 0);

  // Find Monday of the week containing the target date
  const targetDayOfWeek = d.getUTCDay() || 7;
  const targetMonday = new Date(d);
  targetMonday.setUTCDate(d.getUTCDate() - targetDayOfWeek + 1);
  targetMonday.setUTCHours(0, 0, 0, 0);

  // Calculate weeks difference
  const diffMs = targetMonday.getTime() - week1Monday.getTime();
  const diffWeeks = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));

  return diffWeeks + 1; // Week 1 is the first week
}

/**
 * Convert JavaScript's Sunday-first weekday (Date.getDay(): Sun=0..Sat=6) to a
 * Monday-first index (Mon=0..Sun=6). Used by the date-scheduling kanban to bucket
 * tasks into Monday-first weekday columns on both the server (getKanban) and the
 * client (TaskKanbanLightbox).
 */
export function jsDayToMondayFirstIndex(jsDay: number): number {
  return (jsDay + 6) % 7;
}

export type YcClaimErrorCode =
  | "CONFLICT"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "UNAUTHORIZED"
  | string;

export interface YcClaimErrorUi {
  message: string;
  isEmailMismatch: boolean;
}

export function getYcClaimErrorUi(code: YcClaimErrorCode | undefined): YcClaimErrorUi {
  switch (code) {
    case "CONFLICT":
      return {
        isEmailMismatch: true,
        message:
          "You're signed in with a different account than the one that received this invite. Sign out, then continue with the invited email address.",
      };
    case "UNAUTHORIZED":
      return {
        isEmailMismatch: false,
        message:
          "We couldn't find the YC application you were trying to claim. Open your invite link again to continue.",
      };
    case "NOT_FOUND":
      return {
        isEmailMismatch: false,
        message:
          "This YC application link no longer points to an active submission. Open the latest invite link or return to the application.",
      };
    case "FORBIDDEN":
      return {
        isEmailMismatch: false,
        message:
          "This YC application link is no longer available. Open the latest invite link or return to the application.",
      };
    default:
      return {
        isEmailMismatch: false,
        message:
          "We couldn't link your YC submission yet. Please try again from your application link.",
      };
  }
}

/**
 * Rewrites the `[NEEDS FOUNDER INPUT — note about what's missing]` token
 * emitted by the generator prompts for unaddressed sections. The text after
 * the separator is signal (it tells the founder exactly what to add) and must
 * be preserved verbatim; only the brackets go, so the rendered output reads
 * like Lev's voice instead of a debug token.
 *
 * Stored markdown keeps the original bracketed token untouched so it stays
 * machine-parseable for analytics/automation.
 *
 * **Render-time only.** Do NOT apply this when persisting markdown,
 * populating an edit buffer, copying to clipboard, or feeding content back
 * to the model: those paths must see the raw `[NEEDS FOUNDER INPUT …]`
 * tokens. A surface that renders markdown uses
 * `humanizeFounderInputPlaceholders`; one that renders plain text uses
 * `splitFounderInputPlaceholders` and styles the placeholder runs itself
 * (StructuredResultsView), because markdown markers would show literally
 * there.
 */
const FALLBACK_HUMANIZED =
  "You didn't address this yet. Adding a sentence here would strengthen your application.";

function humanizedPlaceholderText(inner: string): string {
  // Strip the leading separator (em-dash, en-dash, hyphen, colon). No inner
  // text means the model gave no note, so the generic copy stands in.
  const note = inner.replace(/^\s*[—–\-:]\s*/, "").trim();
  return note ? `Need more info: ${note}` : FALLBACK_HUMANIZED;
}

export type FounderInputTextPart = { text: string; placeholder: boolean };

export function splitFounderInputPlaceholders(text: string): FounderInputTextPart[] {
  const parts: FounderInputTextPart[] = [];
  let last = 0;
  for (const match of text.matchAll(FOUNDER_INPUT_PLACEHOLDER)) {
    const start = match.index ?? 0;
    if (start > last) parts.push({ text: text.slice(last, start), placeholder: false });
    parts.push({ text: humanizedPlaceholderText(match[1] ?? ""), placeholder: true });
    last = start + match[0].length;
  }
  if (last < text.length || parts.length === 0) {
    parts.push({ text: text.slice(last), placeholder: false });
  }
  return parts;
}

export function humanizeFounderInputPlaceholders(markdown: string): string {
  return markdown.replace(
    FOUNDER_INPUT_PLACEHOLDER,
    (_match, inner: string) => `*${humanizedPlaceholderText(inner)}*`,
  );
}

/** Split an array into chunks of the given size. */
export function chunk<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

/**
 * Convert a verb-first imperative action phrase into its "-ing" continuous
 * form, lowercased. Used to render the founder's current eval gaps as
 * "we're working on this now" focus phrases in the task sidebar
 * (e.g. "Define switching costs" → "defining switching costs").
 *
 * Only the leading verb is transformed; the remaining words are preserved
 * verbatim (so "TAM/SAM/SOM" and the like survive intact). The rules cover
 * the regular English gerund cases that the evaluation's action-phrase titles
 * use in practice:
 *   - drop a trailing silent -e:        define  → defining
 *   - double a final consonant for      run     → running
 *     short consonant-vowel-consonant   map     → mapping
 *     verbs (≤4 chars)
 *   - otherwise append -ing:            strengthen → strengthening
 *
 * Rare irregular verbs may read slightly off — acceptable for a sidebar nudge.
 */
export function toGerund(actionPhrase: string): string {
  const trimmed = actionPhrase.trim();
  if (trimmed.length === 0) return "";

  const words = trimmed.split(/\s+/);
  const verb = (words[0] ?? "").toLowerCase();
  const rest = words.slice(1);

  let gerund: string;
  if (/[^aeiou]e$/.test(verb)) {
    // silent trailing -e (define, validate, size) — drop the e.
    gerund = `${verb.slice(0, -1)}ing`;
  } else if (verb.length <= 4 && /[^aeiou][aeiou][^aeiouwxy]$/.test(verb)) {
    // short consonant-vowel-consonant (run, map) — double the final consonant.
    gerund = `${verb}${verb.slice(-1)}ing`;
  } else {
    gerund = `${verb}ing`;
  }

  return [gerund, ...rest].join(" ");
}

/**
 * Join a list into a natural-language conjunction WITHOUT an Oxford comma:
 *   []            → ""
 *   ["a"]         → "a"
 *   ["a","b"]     → "a and b"
 *   ["a","b","c"] → "a, b and c"
 */
export function joinWithAnd(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * Compose a human-readable location string from discrete city/state/country
 * parts, falling back to a provider-supplied full address when the granular
 * parts are all absent. Used to persist a complete "City, State, Country"
 * value onto Person/Company records from Apollo (and any other provider that
 * returns the same shape) instead of dropping all but the first field
 * (LEV-1049).
 *
 * Returns null when nothing usable is present so callers can leave the column
 * blank (and let a later enrichment pass fill it) rather than writing "".
 */
export function composeLocation(
  parts:
    | {
        city?: string | null;
        state?: string | null;
        country?: string | null;
        rawAddress?: string | null;
      }
    | null
    | undefined,
): string | null {
  if (!parts) return null;
  const composed = [parts.city, parts.state, parts.country]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter((part) => part.length > 0)
    .join(", ");
  if (composed.length > 0) return composed;
  const rawAddress =
    typeof parts.rawAddress === "string" ? parts.rawAddress.trim() : "";
  return rawAddress.length > 0 ? rawAddress : null;
}

/** Promise-based delay. Shared so retry loops don't each define their own. */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Human-readable run duration for a completed task. Compact by default ("2m 14s",
 * "1h 3m", "8s"); `verbose` spells the units out ("2 minutes 14 seconds",
 * "1 hour 3 minutes", "10 seconds") for the task lightbox's "ran for …" label.
 * Sub-minute renders seconds; sub-hour renders minutes (+ seconds when non-zero);
 * an hour or more renders hours + minutes. Negative/NaN inputs (clock skew,
 * missing timestamps) collapse to zero so a caller never shows a garbage span.
 */
export function formatRunDuration(ms: number, opts?: { verbose?: boolean }): string {
  const verbose = opts?.verbose ?? false;
  if (!Number.isFinite(ms) || ms <= 0) return verbose ? "0 seconds" : "0s";
  const totalSeconds = Math.round(ms / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);
  if (verbose) {
    const unit = (n: number, u: string) => `${n} ${u}${n === 1 ? "" : "s"}`;
    if (hours > 0) return `${unit(hours, "hour")} ${unit(minutes, "minute")}`;
    if (totalMinutes > 0)
      return seconds > 0
        ? `${unit(minutes, "minute")} ${unit(seconds, "second")}`
        : unit(minutes, "minute");
    return unit(seconds, "second");
  }
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (totalMinutes > 0) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  return `${seconds}s`;
}


const MAX_CHUNK_LOAD_CAUSE_DEPTH = 5;

/**
 * The wording each bundler/browser pairing throws when a dynamic `import()`
 * resolves to a hashed chunk filename the current deployment no longer
 * serves: webpack's own name, and the phrasing Chrome, Safari and Firefox
 * use for the same failure. Anchored to the start of the message so an
 * unrelated app error that happens to contain "module" or "failed" later in
 * the string doesn't match; the real messages carry a variable URL suffix
 * these patterns deliberately don't require.
 */
const CHUNK_LOAD_ERROR_MESSAGE =
  /^(loading (css )?chunk [\w-]+ failed|failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module)/i;

/**
 * True when `err`, or an error in its `cause` chain, is a stale chunk load
 * failure rather than an application fault. The tab held a build whose chunk
 * manifest a later deploy has already replaced; retrying re-requests the
 * identical missing URL and fails again, so only a refresh (which re-reads
 * the current manifest) recovers. Depth-capped like isNetworkDropError
 * (src/lib/sentry/networkDrop.ts) so a self-referential `cause` can't loop.
 */
export function isChunkLoadError(err: unknown): boolean {
  let current: unknown = err;
  for (let depth = 0; depth < MAX_CHUNK_LOAD_CAUSE_DEPTH; depth++) {
    if (typeof current !== "object" || current === null) return false;
    const { name, message, cause } = current as {
      name?: unknown;
      message?: unknown;
      cause?: unknown;
    };
    if (name === "ChunkLoadError") return true;
    if (typeof message === "string" && CHUNK_LOAD_ERROR_MESSAGE.test(message.trim())) {
      return true;
    }
    if (cause === current) return false;
    current = cause;
  }
  return false;
}

/**
 * True when parsePptxToText / parsePdfToText threw because the deck holds no
 * extractable text (image-only). Callers with a server vision fallback treat
 * this as expected, not a fault.
 */
export function isDeckNoTextError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : "";
  return (
    msg === PPTX_NO_TEXT_ERROR ||
    msg === PPTX_NO_SLIDES_ERROR ||
    msg === PDF_NO_TEXT_ERROR
  );
}

/**
 * A block rendered from onboarding facts, not yet developed. Its only forward
 * action is its Improve chip (enhance); PROTO has no "Looks good" and nothing
 * ever returns a block to PROTO. Compare status through this predicate rather
 * than a raw `=== "PROTO"` so the lifecycle rule has one home.
 */
export const isProtoBlock = (b: { status: DocumentStatus }): boolean =>
  b.status === DOCUMENT_STATUS.PROTO;

/**
 * The BAD_REQUEST message documents.startGeneration / documents.regenerate
 * throw for a PROTO block. The client classifies the refusal by this exact
 * text, so server and client must both import it from here.
 */
export const REGENERATE_PROTO_REFUSAL_MESSAGE =
  "Improve this block instead of regenerating it.";

/**
 * A primary counts as "developed" only once enhance or generation has produced
 * it: a committed version AND a non-PROTO status. A PROTO block carries proto
 * prose (currentVersionId set) but is not developed, so "block exists" must
 * never be read as "developed" — use this at every site that gates on real
 * primary content (rank inputs, the anonymous cap, evaluation sources).
 */
export const isDevelopedPrimary = (b: {
  status: DocumentStatus;
  currentVersionId: string | null;
}): boolean => !isProtoBlock(b) && b.currentVersionId !== null;

/**
 * Maps a primary document's status to its canvas card badge. Returns undefined
 * for deliverables — they never show a status badge.
 *
 * Single source of truth so owner, member, and public canvas all show the same
 * badge for PROTO; PROTO must not fall through to plain "Draft".
 */
export function getPrimaryDocStatus(
  doc: { status: DocumentStatus; hasChangesSinceApproval?: boolean | null },
  isDeliverable: boolean,
): { label: string; tone: "draft" | "approved" | "warning" } | undefined {
  if (isDeliverable) return undefined;
  if (doc.status === DOCUMENT_STATUS.PROTO) {
    return { label: "Early Draft", tone: "draft" };
  }
  if (doc.status === DOCUMENT_STATUS.APPROVED) {
    return doc.hasChangesSinceApproval
      ? { label: "Needs Re-approve", tone: "warning" }
      : { label: "Approved", tone: "approved" };
  }
  return { label: "Draft", tone: "draft" };
}

const REDIRECT_PARSE_ORIGIN = "https://placeholder.invalid";

/**
 * Returns `value` as a normalized same-site path, or null when it could lead
 * off-site. Browsers strip tabs and newlines and read `\` as `/` before
 * resolving, so `/\evil.com` and `/<TAB>/evil.com` both become the
 * scheme-relative `//evil.com`; rejecting every backslash and control
 * character closes that family. Dot segments (`/..//evil.com`) collapse to
 * `//evil.com` only after parsing, and Clerk strips the origin before it
 * `router.push`es, so the pathname is checked and returned in its normalized
 * form: the raw input is never what gets followed.
 */
export function safeRedirectPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code === 0x5c || code <= 0x1f || code === 0x7f) return null;
  }
  let url: URL;
  try {
    url = new URL(value, REDIRECT_PARSE_ORIGIN);
  } catch {
    /* expected: an unparseable redirect is simply not followed */
    return null;
  }
  if (url.origin !== REDIRECT_PARSE_ORIGIN || url.pathname.startsWith("//")) return null;
  return url.pathname + url.search + url.hash;
}
